import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Transition from '@/utils/Transition';
import moment from 'moment';
import classNames from 'classnames';
import ModalAction from '@/components/ModalAction';
import { useWindowWidth } from '@/hooks/useWindowWidth';

const SimpleDropdown = ({ triggerSimple, dropdownSimple, dropdownSimpleOpen, setDropdownSimpleOpen, periodOptions, selectedSimple, getDropdownPosition, closeDropdowns, setSelectedSimple }) => {
  const windowWidth = useWindowWidth();
  const Container = windowWidth > 768 ? React.Fragment : 'div';

  return (
    <Container {...(windowWidth > 768 ? {} : { className: "relative md:static" })}>
      <div className="inline-flex min-w-full md:min-w-[22%]">
        <button
            ref={triggerSimple}
            className={classNames(
              'dropdown justify-between w-full py-2.5 md:py-[7px]',
              dropdownSimpleOpen ? 'bg-[#F8F8F7]' : 'bg-white'
            )}
            aria-label="Select date range"
            aria-haspopup="true"
            onClick={() => { closeDropdowns(); setDropdownSimpleOpen(!dropdownSimpleOpen) }}
            aria-expanded={dropdownSimpleOpen}
        >
          <span className="flex items-center leading-[1.2]">
            <span>{periodOptions[selectedSimple].title}</span>
          </span>
          <svg className={classNames(
            'min-w-4 w-4 h-4 ml-1',
            dropdownSimpleOpen ? 'rotate-180' : ''
          )} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.4" strokeMiterlimit="10" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      {/* Simple Dropdown Content */}
      <Transition
          show={dropdownSimpleOpen}
          tag="div"
          className="z-10 absolute md:!top-full left-0 md:left-4 w-full bg-white border border-[#F2F2F2] rounded-[8px] md:shadow-[2px_2px_8px_2px_#5252521F] overflow-hidden py-1 md:mt-2 md:min-w-[160px]"
          enter="transition ease-out duration-100 transform"
          enterStart="opacity-0 -translate-y-2"
          enterEnd="opacity-100 translate-y-0"
          leave="transition ease-out duration-100"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
          style={{ ...getDropdownPosition(triggerSimple) }}
      >
        <div
            ref={dropdownSimple}
            className="font-medium text-sm text-slate-600"
            onFocus={() => setDropdownSimpleOpen(true)}
            onBlur={() => setDropdownSimpleOpen(false)}
        >
          {periodOptions.map(option => {
            return (
              <button
                  key={option.id}
                  tabIndex="0"
                  className={`flex items-center group w-full text-sm md:text-[13px] px-3 py-2.5 ${option.id === selectedSimple ? 'text-brand-green' : 'text-brand-green'}`}
                  onClick={() => { setSelectedSimple(option.id); setDropdownSimpleOpen(false); }}
              >
                <div className="shrink-0 mr-2 w-5 md:w-4 h-5 md:h-4 flex items-center justify-center">
                  <div className={`w-full h-full rounded-full border ${option.id === selectedSimple ? 'border-brand-green' : 'border-[#E8E8E8] group-hover:border-brand-green'} flex items-center justify-center transition-all duration-200`}>
                    {option.id === selectedSimple && (
                      <div className="w-2.5 md:w-2 h-2.5 md:h-2 rounded-full bg-brand-green"></div>
                    )}
                  </div>
                </div>
                <span className="font-normal">{option.title}</span>
              </button>
            )
          })}
        </div>
      </Transition>
    </Container>
  );
}

const ComparedDropdown = ({ triggerCompared, dropdownCompared, dropdownComparedOpen, setDropdownComparedOpen, comparedToOptions, selectedCompared, getDropdownPosition, closeDropdowns, comparedToVisible, periodOptions, selectedSimple, setSelectedCompared }) => {
  const windowWidth = useWindowWidth();
  const Container = windowWidth > 768 ? React.Fragment : 'div';

  return (
    <Container {...(windowWidth > 768 ? {} : { className: "relative md:static" })}>
      {comparedToVisible && periodOptions[selectedSimple].id !== 8 && (
        <>
          <div className="md:pl-4 xl:pl-[8%] md:pr-4 text-sm font-medium self-center mb-4 md:mb-0 mr-auto">Compared to</div>
          <div className="relative inline-flex min-w-full md:min-w-[22%]">
            <button
                ref={triggerCompared}
                className={classNames(
                  'dropdown justify-between w-full py-2.5 md:py-[7px]',
                  dropdownComparedOpen ? 'bg-[#F8F8F7]' : 'bg-white'
                )}
                aria-label="Select date range"
                aria-haspopup="true"
                onClick={() => { closeDropdowns(); setDropdownComparedOpen(!dropdownComparedOpen) }}
                aria-expanded={dropdownComparedOpen}
            >
              <span className="flex items-center leading-[1.2]">
                <span>{comparedToOptions[selectedCompared].title}</span>
              </span>
              <svg className={classNames(
                'min-w-4 w-4 h-4 ml-1',
                dropdownComparedOpen ? 'rotate-180' : ''
              )} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.4" strokeMiterlimit="10" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </>
      )}
      {/* Compared Dropdown Content */}
      <Transition
          show={dropdownComparedOpen}
          tag="div"
          className="z-10 absolute !top-full left-0 md:left-4 w-full bg-white border border-[#F2F2F2] rounded-[8px] md:shadow-[2px_2px_8px_2px_#5252521F] overflow-hidden py-1 md:mt-2 md:min-w-[340px]"
          enter="transition ease-out duration-100 transform"
          enterStart="opacity-0 -translate-y-2"
          enterEnd="opacity-100 translate-y-0"
          leave="transition ease-out duration-100"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
          style={{ ...getDropdownPosition(triggerCompared) }}
      >
        <div
            ref={dropdownCompared}
            className="font-medium text-sm text-slate-600"
            onFocus={() => setDropdownComparedOpen(true)}
            onBlur={() => setDropdownComparedOpen(false)}
        >
          {comparedToOptions.map(option => {
            return (
              <button
                  key={option.id}
                  tabIndex="0"
                  className={`flex items-center group w-full text-sm md:text-[13px] px-3 py-2.5 ${option.id === selectedCompared ? 'text-brand-green' : 'text-brand-green'}`}
                  onClick={() => { setSelectedCompared(option.id); setDropdownComparedOpen(false); }}
              >
                <div className="shrink-0 mr-2 w-5 md:w-4 h-5 md:h-4 flex items-center justify-center">
                  <div className={`w-full h-full rounded-full border ${option.id === selectedCompared ? 'border-brand-green' : 'border-[#E8E8E8] group-hover:border-brand-green'} flex items-center justify-center transition-all duration-200`}>
                    {option.id === selectedCompared && (
                      <div className="w-2.5 md:w-2 h-2.5 md:h-2 rounded-full bg-brand-green"></div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col md:flex-row items-start">
                  <span className="font-normal">{option.title}</span>
                  {option.date && option.date.start && option.date.end && option.title !== 'No comparison' && (
                    <div className="flex mt-px ml-0 md:ml-3 text-[#A5A5A5] text-[12px]">
                      {option.title !== 'Custom' && `${moment(option.date.start).format(moment().year() === moment(option.date.start).year() ? 'DD MMM' : 'DD MMM YYYY')} – ${moment(option.date.end).format(moment().year() === moment(option.date.end).year() ? 'DD MMM' : 'DD MMM YYYY')}`}
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </Transition>
    </Container>
  );
}

const UnitDropdown = ({ triggerUnit, dropdownUnit, dropdownUnitOpen, setDropdownUnitOpen, unitOptions, selectedUnit, getDropdownPosition, closeDropdowns, setSelectedUnit }) => {
  const windowWidth = useWindowWidth();
  const Container = windowWidth > 768 ? React.Fragment : 'div';

  return (
    <Container {...(windowWidth > 768 ? {} : { className: "relative md:static" })}>
      <div className="md:mr-2.5 md:ml-2.5 relative inline-flex min-w-full md:min-w-[10%]">
        <button
            ref={triggerUnit}
            className={classNames(
              'dropdown justify-between w-full py-2.5 md:py-[7px]',
              dropdownUnitOpen ? 'bg-[#F8F8F7]' : 'bg-white'
            )}
            aria-label="Select date range"
            aria-haspopup="true"
            onClick={() => { closeDropdowns(); setDropdownUnitOpen(!dropdownUnitOpen) }}
            aria-expanded={dropdownUnitOpen}
        >
            <span className="flex items-center leading-[1.2]">
              <span>{unitOptions[selectedUnit].title}</span>
            </span>
            <svg 
            className={classNames(
              'min-w-4 w-4 h-4 ml-1',
              dropdownUnitOpen ? 'rotate-180' : ''
            )}
            width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.4" strokeMiterlimit="10" strokeLinecap="round"/>
            </svg>
        </button>
      </div>
      {/* Unit Dropdown Content */}
      <Transition
          show={dropdownUnitOpen}
          tag="div"
          className="z-10 absolute !top-full left-0 md:left-4 w-full bg-white border border-[#F2F2F2] rounded-[8px] md:shadow-[2px_2px_8px_2px_#5252521F] overflow-hidden py-1 md:mt-2 md:min-w-[120px]"
          enter="transition ease-out duration-100 transform"
          enterStart="opacity-0 -translate-y-2"
          enterEnd="opacity-100 translate-y-0"
          leave="transition ease-out duration-100"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
          style={{ ...getDropdownPosition(triggerUnit) }}
      >
        <div
            ref={dropdownUnit}
            className="font-medium text-sm text-slate-600"
            onFocus={() => setDropdownUnitOpen(true)}
            onBlur={() => setDropdownUnitOpen(false)}
        >
          {unitOptions.map(option => {
            if (option.visible) {
              return (
                <button
                    key={option.id}
                    tabIndex="0"
                    className={`flex items-center group w-full text-sm md:text-[13px] px-3 py-2.5 ${option.id === selectedUnit ? 'text-brand-green' : 'text-brand-green'}`}
                    onClick={() => { setSelectedUnit(option.id); setDropdownUnitOpen(false); }}
                >
                  <div className="shrink-0 mr-2 w-5 md:w-4 h-5 md:h-4 flex items-center justify-center">
                    <div className={`w-full h-full rounded-full border ${option.id === selectedUnit ? 'border-brand-green' : 'border-[#E8E8E8] group-hover:border-brand-green'} flex items-center justify-center transition-all duration-200`}>
                      {option.id === selectedUnit && (
                        <div className="w-2.5 md:w-2 h-2.5 md:h-2 rounded-full bg-brand-green"></div>
                      )}
                    </div>
                  </div>
                  <span className="font-normal">{option.title}</span>
                </button>
              )
            }
            return null;
          })}
        </div>
      </Transition>
    </Container>
  );
}

const PaymentOptions = ({ paymentOptions, paymentOptionFilters, setPaymentOptionFilters, applyFilter, resetFilter, setDropdownFilterOpen }) => {
  return (
    <>
      <div className="grid grid-cols-2 gap-x-3">
          <div>
              {paymentOptions.filter(po => !po.key.includes('Test') && 
                  (po.key.includes('stripe') || po.key.includes('PayPal') || po.key.includes('skrill') || 
                  po.key.includes('paystack') || po.key.includes('razorpay'))).map((po, index) => (
                  <div key={`dropdown-payment-option-${index}`} className="flex items-center py-2 first:pt-0 mb-1">
                      <label className="flex items-center cursor-pointer select-none text-[11px] md:text-[13px] leading-[1.4]">
                          <div className={`w-5 md:w-4 min-w-5 md:min-w-4 h-5 md:h-4 rounded-[3px] flex items-center justify-center mr-2 ${paymentOptionFilters[po.key] ? 'bg-[#3CBBCE] text-white' : 'bg-white border border-[#E8E8E8]'}`}>
                              {paymentOptionFilters[po.key] && (
                                <svg className="w-full h-full" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5 7.65517L7.21739 10L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                          </div>
                          <input 
                              type="checkbox" 
                              className="hidden" 
                              name={po.key} 
                              checked={paymentOptionFilters[po.key]} 
                              onChange={() => setPaymentOptionFilters(state => ({...state, [po.key]: !state[po.key]}))} 
                          />
                          <span className="whitespace-nowrap">{po.title}</span>
                      </label>
                  </div>
              ))}

              {paymentOptions.filter(po => 
                  po.key.includes('trial') || 
                  po.key.includes('coinpayments') ||
                  po.key.includes('telegram') || 
                  po.key.includes('access code')).map((po, index) => (
                  <div key={`dropdown-payment-option-other-${index}`} className="flex items-center py-2 first:pt-0 mb-1">
                      <label className="flex items-center cursor-pointer select-none text-[11px] md:text-[13px] leading-[1.4]">
                          <div className={`w-5 md:w-4 min-w-5 md:min-w-4 h-5 md:h-4 rounded-[3px] flex items-center justify-center mr-2 ${paymentOptionFilters[po.key] ? 'bg-[#3CBBCE] text-white' : 'bg-white border border-[#E8E8E8]'}`}>
                              {paymentOptionFilters[po.key] && (
                                <svg className="w-full h-full" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5 7.65517L7.21739 10L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                          </div>
                          <input 
                              type="checkbox" 
                              className="hidden" 
                              name={po.key} 
                              checked={paymentOptionFilters[po.key]} 
                              onChange={() => setPaymentOptionFilters(state => ({...state, [po.key]: !state[po.key]}))} 
                          />
                          <span className="whitespace-nowrap">{po.title}</span>
                      </label>
                  </div>
              ))}
          </div>

          <div>
              {paymentOptions.filter(po => po.key.includes('Test')).map((po, index) => (
                  <div key={`dropdown-payment-option-test-${index}`} className="flex items-center py-2 first:pt-0 mb-1">
                      <label className="flex items-center cursor-pointer select-none text-[11px] md:text-[13px] leading-[1.4]">
                          <div className={`w-5 md:w-4 min-w-5 md:min-w-4 h-5 md:h-4 rounded-[3px] flex items-center justify-center mr-2 ${paymentOptionFilters[po.key] ? 'bg-[#3CBBCE] text-white' : 'bg-white border border-[#E8E8E8]'}`}>
                              {paymentOptionFilters[po.key] && (
                                <svg className="w-full h-full" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5 7.65517L7.21739 10L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                          </div>
                          <input 
                              type="checkbox" 
                              className="hidden" 
                              name={po.key} 
                              checked={paymentOptionFilters[po.key]} 
                              onChange={() => setPaymentOptionFilters(state => ({...state, [po.key]: !state[po.key]}))} 
                          />
                          <span className="whitespace-nowrap">{po.title}</span>
                      </label>
                  </div>
              ))}
          </div>
      </div>
    </>
  )
}

const FilterDropdown = ({ triggerFilter, dropdownFilter, dropdownFilterOpen, setDropdownFilterOpen, paymentOptions, getDropdownPosition, closeDropdowns, paymentOptionFilters, setPaymentOptionFilters, applyFilter, resetFilter }) => {
  const windowWidth = useWindowWidth();
  const Container = windowWidth > 768 ? React.Fragment : 'div';

  return (
    <Container {...(windowWidth > 768 ? {} : { className: "relative md:static" })}>
      <div className="relative inline-flex">
        <button
            ref={triggerFilter}
            className={classNames(
              'dropdown w-8 h-8 p-0 relative',
              dropdownFilterOpen ? 'bg-[#F8F8F7]' : 'bg-white'
            )}
            aria-haspopup="true"
            onClick={() => { closeDropdowns(); setDropdownFilterOpen(!dropdownFilterOpen) }}
            aria-expanded={dropdownFilterOpen}
        >
          <span className="sr-only">Filter</span><wbr />
          <svg className="w-[14px] h-3" width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1H15" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round"/>
            <path d="M2.75 7H13.25" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round"/>
            <path d="M5.375 13H10.625" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round"/>
          </svg>
          {Object.entries(paymentOptionFilters).filter((payment) => !payment[1]).length > 0 && <div className="absolute top-1 right-1 w-2 h-2 bg-rose-500 border-[1px] border-white rounded-full"></div>}
        </button>
      </div>
      {/* Filter Dropdown Content */}
      <Transition
          show={dropdownFilterOpen}
          tag="div"
          className="z-10 absolute !top-full !right-0 w-full bg-white border border-[#F2F2F2] rounded-[8px] md:shadow-[2px_2px_8px_2px_#5252521F] overflow-hidden mt-2 md:min-w-[325px]"
          enter="transition ease-out duration-200 transform"
          enterStart="opacity-0 -translate-y-2"
          enterEnd="opacity-100 translate-y-0"
          leave="transition ease-out duration-200"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
          style={{ ...getDropdownPosition(triggerFilter, 'right') }}
      >
        <div ref={dropdownFilter} className="px-3 pt-3">
          <PaymentOptions
            paymentOptions={paymentOptions}
            paymentOptionFilters={paymentOptionFilters}
            setPaymentOptionFilters={setPaymentOptionFilters}
            applyFilter={applyFilter}
            resetFilter={resetFilter}
            setDropdownFilterOpen={setDropdownFilterOpen}
          />
          
          <div className="flex flex-col justify-center mt-5 md:mt-9 mb-1.5 gap-1.5">
            <button 
                className="w-full text-white btn-primary text-sm font-medium pt-[14px] pb-4 transition-colors duration-200" 
                onClick={applyFilter} 
                onBlur={() => setDropdownFilterOpen(false)}
            >
              Apply
            </button>
            <button
                className="w-full text-brand-green  btn-primary bg-white hover:bg-[#F8F8F7] border-white hover:border-[#F8F8F7] text-sm font-medium pt-[14px] pb-4 transition-colors duration-200"
                onClick={resetFilter}
                onBlur={() => setDropdownFilterOpen(false)}
            >
              Clear all
            </button>
          </div>
        </div>
      </Transition>
    </Container>
  );
}

const DropdownScrollGroup = React.memo(forwardRef(({ periodOptions, comparedToOptions, unitOptions, onPeriodChange, onComparedToChange, onUnitChange, onFilterChange, period, comparedToVisible, comparedToDatepickerVisible, comparedCustom, setComparedCustom }, _ref) => {
  const badgeClasses = 'text-[11px] md:text-[13px] font-medium leading-[1.2] py-1 md:py-0.5 px-1.5 md:px-1 bg-[#F2F2F2] ml-1 md:ml-1.5 rounded-[4px]';
  const paymentOptions = [
    { key: "stripeLive", title: 'Stripe' },
    { key: "PayPalLive", title: 'PayPal' },
    { key: "skrillLive", title: 'Skrill' },
    { key: "paystackLive", title: 'Paystack' },
    { key: "razorpayLive", title: 'Razorpay' },
    { key: "free trial", title: 'Free trial (no-CC)' },
    { key: "coinpayments", title: 'CoinPayments' },
    { key: "telegram", title: 'Telegram Stars' },
    { key: "access code (single-use)", title: 'Access Code (single-use)' },
    { key: "access code (multi-use)", title: 'Access Code (multi-use)' },
    { key: "stripeTest", title: <>Stripe <span className={badgeClasses}>Test</span></> },
    { key: "PayPalTest", title: <>PayPal <span className={badgeClasses}>Test</span></> },
    { key: "skrillTest", title: <>Skrill <span className={badgeClasses}>Test</span></> },
    { key: "paystackTest", title: <>Paystack <span className={badgeClasses}>Test</span></> },
    { key: "razorpayTest", title: <>Razorpay <span className={badgeClasses}>Test</span></> },
  ]

  const [paymentOptionFilters, setPaymentOptionFilters] = useState(Object.fromEntries(paymentOptions.map(p => [p.key, true])));

  const containerRef = useRef(null);

  // simple dropdown
  const [dropdownSimpleOpen, setDropdownSimpleOpen] = useState(false);
  const [selectedSimple, setSelectedSimple] = useState(1);

  const triggerSimple = useRef(null);
  const dropdownSimple = useRef(null);

  // mobile refs
  const triggerSimpleMobile = useRef(null);
  const dropdownSimpleMobile = useRef(null);
  const [dropdownSimpleMobileOpen, setDropdownSimpleMobileOpen] = useState(false);
  
  // compared dropdown
  const [dropdownComparedOpen, setDropdownComparedOpen] = useState(false);
  const [selectedCompared, setSelectedCompared] = useState(0);

  const triggerCompared = useRef(null);
  const dropdownCompared = useRef(null);
  
  // mobile compared refs
  const triggerComparedMobile = useRef(null);
  const dropdownComparedMobile = useRef(null);
  const [dropdownComparedMobileOpen, setDropdownComparedMobileOpen] = useState(false);

  // unit dropdown
  const [dropdownUnitOpen, setDropdownUnitOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(0);

  const triggerUnit = useRef(null);
  const dropdownUnit = useRef(null);
  
  // mobile unit refs
  const triggerUnitMobile = useRef(null);
  const dropdownUnitMobile = useRef(null);
  const [dropdownUnitMobileOpen, setDropdownUnitMobileOpen] = useState(false);
  
  // filter dropdown
  const [dropdownFilterOpen, setDropdownFilterOpen] = useState(false);

  const triggerFilter = useRef(null);
  const dropdownFilter = useRef(null);
  
  // mobile filter refs
  const triggerFilterMobile = useRef(null);
  const dropdownFilterMobile = useRef(null);
  const [dropdownFilterMobileOpen, setDropdownFilterMobileOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalError, setModalError] = useState(null);

  // close mobile dropdowns
  const closeMobileDropdowns = () => {
    setDropdownSimpleMobileOpen(false);
    setDropdownComparedMobileOpen(false);
    setDropdownUnitMobileOpen(false);
    setDropdownFilterMobileOpen(false);
  }

  const windowWidth = useWindowWidth();
  
  // close on click outside
  useEffect(() => {
    const clickHandlerSimple = ({ target }) => {
      if (!dropdownSimple.current) return;
      if (!dropdownSimpleOpen || dropdownSimple.current.contains(target) || triggerSimple.current.contains(target)) return;
      setDropdownSimpleOpen(false)
    };
    const clickHandlerCompared = ({ target }) => {
      if (!dropdownCompared.current) return;
      if (!dropdownComparedOpen || dropdownCompared.current.contains(target) || triggerCompared.current.contains(target)) return;
      setDropdownComparedOpen(false);
    };
    const clickHandlerUnit = ({ target }) => {
      if (!dropdownUnit.current) return;
      if (!dropdownUnitOpen || dropdownUnit.current.contains(target) || triggerUnit.current.contains(target)) return;
      setDropdownUnitOpen(false);
    };
    const clickHandlerFilter = ({ target }) => {
      if (!dropdownFilter.current) return;
      if (!dropdownFilterOpen || dropdownFilter.current.contains(target) || triggerFilter.current.contains(target)) return;
      setDropdownFilterOpen(false);
    };
    
    // Mobile handlers
    const clickHandlerSimpleMobile = ({ target }) => {
      if (!dropdownSimpleMobile.current) return;
      if (!dropdownSimpleMobileOpen || dropdownSimpleMobile.current.contains(target) || triggerSimpleMobile.current.contains(target)) return;
      setDropdownSimpleMobileOpen(false)
    };
    const clickHandlerComparedMobile = ({ target }) => {
      if (!dropdownComparedMobile.current) return;
      if (!dropdownComparedMobileOpen || dropdownComparedMobile.current.contains(target) || triggerComparedMobile.current.contains(target)) return;
      setDropdownComparedMobileOpen(false);
    };
    const clickHandlerUnitMobile = ({ target }) => {
      if (!dropdownUnitMobile.current) return;
      if (!dropdownUnitMobileOpen || dropdownUnitMobile.current.contains(target) || triggerUnitMobile.current.contains(target)) return;
      setDropdownUnitMobileOpen(false);
    };
    const clickHandlerFilterMobile = ({ target }) => {
      if (!dropdownFilterMobile.current) return;
      if (!dropdownFilterMobileOpen || dropdownFilterMobile.current.contains(target) || triggerFilterMobile.current.contains(target)) return;
      setDropdownFilterMobileOpen(false);
    };
    
    document.addEventListener('click', clickHandlerSimple);
    document.addEventListener('click', clickHandlerCompared);
    document.addEventListener('click', clickHandlerUnit);
    document.addEventListener('click', clickHandlerFilter);
    
    // Add mobile handlers
    document.addEventListener('click', clickHandlerSimpleMobile);
    document.addEventListener('click', clickHandlerComparedMobile);
    document.addEventListener('click', clickHandlerUnitMobile);
    document.addEventListener('click', clickHandlerFilterMobile);
    
    return () => {
      document.removeEventListener('click', clickHandlerSimple);
      document.removeEventListener('click', clickHandlerCompared);
      document.removeEventListener('click', clickHandlerUnit);
      document.removeEventListener('click', clickHandlerFilter);
      
      // Remove mobile handlers
      document.removeEventListener('click', clickHandlerSimpleMobile);
      document.removeEventListener('click', clickHandlerComparedMobile);
      document.removeEventListener('click', clickHandlerUnitMobile);
      document.removeEventListener('click', clickHandlerFilterMobile);
    };
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownSimpleOpen || keyCode !== 27) return;
      setDropdownSimpleOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    onPeriodChange(periodOptions[selectedSimple]);
  }, [selectedSimple]);

  useEffect(() => {
    onComparedToChange(comparedToOptions[selectedCompared])
  }, [selectedCompared]);

  useEffect(() => {
    onUnitChange(unitOptions[selectedUnit]);
  }, [selectedUnit]);

  useEffect(() => {
    const visible = unitOptions.filter(option => option.visible);

    if (visible.length) {
      setSelectedUnit(visible[0].id);
    }
  }, [unitOptions]);

  // useEffect(() => {
  //   setSelectedUnit(0);
  // }, [period]);

  useImperativeHandle(_ref, () => ({
    getSelectedPeriod: () => {
      return periodOptions[selectedSimple];
    },
    getSelectedCompared: () => {
      return comparedToOptions[selectedCompared];
    },
    getSelectedUnit: () => {
      return unitOptions[selectedUnit];
    },
  }));

  const closeDropdowns = () => {
    setDropdownSimpleOpen(false);
    setDropdownComparedOpen(false);
    setDropdownUnitOpen(false);
    setDropdownFilterOpen(false);
  }

  const getDropdownPosition = (ref, align) => {
    const isMobile = windowWidth < 768;
    let left;
    let right;
    let top;
    let width = 200;

    if (containerRef.current && ref.current) {
      top = Math.abs(containerRef.current.getBoundingClientRect().y - ref.current.getBoundingClientRect().y + ref.current.clientHeight);
      width = ref.current.offsetWidth;

      if (align && align === 'right') {
        right = Math.abs(containerRef.current.getBoundingClientRect().right - ref.current.getBoundingClientRect().right)
      } else {
        left = Math.abs(containerRef.current.getBoundingClientRect().left - ref.current.getBoundingClientRect().left) + 16;
      }

      if (isMobile) {
        return {
          top,
        }
      }

      return {
        left,
        right,
        top,
        width,
      }
    }
    return {};
  }

  const applyFilter = () => {
    setDropdownFilterOpen(false);
    onFilterChange(JSON.stringify(paymentOptionFilters));
  }

  const resetFilter = () => {
    const defaultFilters = Object.fromEntries(paymentOptions.map(p => [p.key, true]));
    setPaymentOptionFilters(defaultFilters);
    setDropdownFilterOpen(false);
    onFilterChange(JSON.stringify(defaultFilters));
  }

  const resetAll = () => {
    const defaultPeriodIndex = 1;
    const defaultComparedIndex = 0;
    const defaultUnitIndex = 0;

    setSelectedSimple(defaultPeriodIndex);
    setSelectedCompared(defaultComparedIndex);
    setSelectedUnit(defaultUnitIndex);
    
    closeDropdowns();

    resetFilter();
  }

  const onClearModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      closeMobileDropdowns();
      setModalError(null);
    }, 200);
  };

  return (
    <>
      <div className="relative w-full p-4 bg-white rounded-[12px] mb-4 hidden md:block">
        {windowWidth >= 768 && (
          <div ref={containerRef} className="flex flex-row items-center whitespace-nowrap overflow-x-auto">
            <SimpleDropdown
              triggerSimple={triggerSimple}
              dropdownSimple={dropdownSimple}
              dropdownSimpleOpen={dropdownSimpleOpen}
              setDropdownSimpleOpen={setDropdownSimpleOpen}
              periodOptions={periodOptions}
              selectedSimple={selectedSimple}
              getDropdownPosition={getDropdownPosition}
              closeDropdowns={closeDropdowns}
              setSelectedSimple={setSelectedSimple}
            />

            <ComparedDropdown
              triggerCompared={triggerCompared}
              dropdownCompared={dropdownCompared}
              dropdownComparedOpen={dropdownComparedOpen}
              setDropdownComparedOpen={setDropdownComparedOpen}
              comparedToOptions={comparedToOptions}
              selectedCompared={selectedCompared}
              getDropdownPosition={getDropdownPosition}
              closeDropdowns={closeDropdowns}
              comparedToVisible={comparedToVisible}
              periodOptions={periodOptions}
              selectedSimple={selectedSimple}
              setSelectedCompared={setSelectedCompared}
            />

            <UnitDropdown
              triggerUnit={triggerUnit}
              dropdownUnit={dropdownUnit}
              dropdownUnitOpen={dropdownUnitOpen}
              setDropdownUnitOpen={setDropdownUnitOpen}
              unitOptions={unitOptions}
              selectedUnit={selectedUnit}
              getDropdownPosition={getDropdownPosition}
              closeDropdowns={closeDropdowns}
              setSelectedUnit={setSelectedUnit}
            />

            <div className="flex-1"></div>
            
            <FilterDropdown
              triggerFilter={triggerFilter}
              dropdownFilter={dropdownFilter}
              dropdownFilterOpen={dropdownFilterOpen}
              setDropdownFilterOpen={setDropdownFilterOpen}
              paymentOptions={paymentOptions}
              getDropdownPosition={getDropdownPosition}
              closeDropdowns={closeDropdowns}
              paymentOptionFilters={paymentOptionFilters}
              setPaymentOptionFilters={setPaymentOptionFilters}
              applyFilter={applyFilter}
              resetFilter={resetFilter}
            />

            <div className="bg-[#E8E8E8] w-px h-auto self-stretch ml-4 xl:ml-6" />
            <button 
              className="text-brand-green text-sm font-medium leading-[1.2] ml-2 xl:ml-4 px-2 py-2"
              onClick={resetAll}
            >Reset</button>
          </div>
        )}
      </div>
      <button
      className="flex items-center gap-5 md:hidden py-2 px-3 bg-white border border-[#F2F2F2] rounded-[6px] mb-4 ml-auto text-brand-green text-[13px] font-medium leading-[1.2]"
      onClick={() => setModalOpen(true)}>
        Filters
        <div className="">
          <svg className="w-[14px] h-3" width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1H15" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round"/>
            <path d="M2.75 7H13.25" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round"/>
            <path d="M5.375 13H10.625" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round"/>
          </svg>
        </div>
      </button>


      {windowWidth < 768 && (
        <ModalAction
          id="filter-mobile-modal"
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          onModalClose={() => onClearModal()}
          size={undefined}
        >
          <div className="text-sm font-medium mb-5">
            Filters
          </div>

          <div className="flex gap-4 flex-col">
            <SimpleDropdown
              triggerSimple={triggerSimple}
              dropdownSimple={dropdownSimple}
              dropdownSimpleOpen={dropdownSimpleOpen}
              setDropdownSimpleOpen={setDropdownSimpleOpen}
              periodOptions={periodOptions}
              selectedSimple={selectedSimple}
              getDropdownPosition={getDropdownPosition}
              closeDropdowns={closeDropdowns}
              setSelectedSimple={setSelectedSimple}
            />

            <ComparedDropdown
              triggerCompared={triggerCompared}
              dropdownCompared={dropdownCompared}
              dropdownComparedOpen={dropdownComparedOpen}
              setDropdownComparedOpen={setDropdownComparedOpen}
              comparedToOptions={comparedToOptions}
              selectedCompared={selectedCompared}
              getDropdownPosition={getDropdownPosition}
              closeDropdowns={closeDropdowns}
              comparedToVisible={comparedToVisible}
              periodOptions={periodOptions}
              selectedSimple={selectedSimple}
              setSelectedCompared={setSelectedCompared}
            />

            <UnitDropdown
              triggerUnit={triggerUnit}
              dropdownUnit={dropdownUnit}
              dropdownUnitOpen={dropdownUnitOpen}
              setDropdownUnitOpen={setDropdownUnitOpen}
              unitOptions={unitOptions}
              selectedUnit={selectedUnit}
              getDropdownPosition={getDropdownPosition}
              closeDropdowns={closeDropdowns}
              setSelectedUnit={setSelectedUnit}
            />

            <div className="flex flex-row justify-between mb-2 mt-4">
              <span className="text-sm font-medium">Payment options</span>
              <button className="text-[11px] leading-none font-medium border-b border-b-brand-green" onClick={resetFilter}>Clear all</button>
            </div>
            <PaymentOptions
              paymentOptions={paymentOptions}
              paymentOptionFilters={paymentOptionFilters}
              setPaymentOptionFilters={setPaymentOptionFilters}
              applyFilter={applyFilter}
              resetFilter={resetFilter}
              setDropdownFilterOpen={setDropdownFilterOpen}
            />
            
            <div className="flex flex-col justify-center gap-3">
              <button 
                className="w-full text-white btn-primary text-[13px] font-medium pt-[14px] pb-4 transition-colors duration-200" 
                onClick={() => {
                  applyFilter();
                  setModalOpen(false);
                }}
              >
                Apply
              </button>
              <button
                className="w-full text-brand-green btn-primary text-[13px] bg-white hover:bg-[#F8F8F7] border-[#F8F8F7] hover:border-[#F8F8F7] font-medium pt-[14px] pb-4 transition-colors duration-200"
                onClick={resetAll}
              >
                Reset
              </button>
            </div>
          </div>
        </ModalAction>
      )}
    </>
  );
}));

export default DropdownScrollGroup;
